import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Lightning,
  ChartLineUp,
  HandCoins,
  Handshake,
  Scales,
  Lock,
  Clock,
  CheckCircle,
  UserPlus,
  FileText,
  Coins,
  EnvelopeSimple,
  Phone,
  Printer,
  MapPin,
  List,
  X,
  Calculator,
  Quotes,
  Star,
  Plus,
  Minus,
  Headset,
  SignIn,
  ArrowUpRight,
  SealCheck,
  Globe,
} from "@phosphor-icons/react";
import { simulate } from "../lib/loanMath";
import { formatCLP, formatThousands } from "../lib/format";

/* ====== Marca EFE - CAPITAL ====== */
const BRAND = {
  deep: "#0B7A3E", // verde profundo (botones / texto marca)
  deep2: "#083E22", // verde muy oscuro (gradientes)
  bright: "#00BE5A", // verde brillante (acentos / highlights)
  leaf: "#33D17A", // verde hoja
  soft: "#E3F4E9", // verde suave (fondos de chips/iconos)
  bg: "#F5F8F6", // fondo general
  ink: "#0D1B12", // texto principal
};

const EMAIL = "contacto@efe-capital.lat";
const CMF_LINK =
  "https://www.cmfchile.cl/institucional/mercados/entidad.php?auth=&send=&mercado=O&rut=96769070&grupo=&tipoentidad=RGEIN&vig=VI&row=AAAwy2ACTAAABy9AAY&control=svs&pestania=1";

const CMF = {
  rut: "96.769.070-8",
  razon: "EFE ARICA - LA PAZ S.A.",
  domicilio: "Morandé 115, Piso 6 · Santiago · Metropolitana",
  inscripcion: "590",
  fecha: "30/03/2021",
  telefono: "225881",
  fax: "231782",
  tipo: "RGEIN · Mercado O",
  vigencia: "Vigente",
};

const NAV = [
  { href: "#beneficios", label: "Beneficios" },
  { href: "#como-funciona", label: "Cómo funciona" },
  { href: "#simulador", label: "Simulador" },
  { href: "#testimonios", label: "Testimonios" },
  { href: "#preguntas", label: "Preguntas" },
  { href: "#contacto", label: "Contacto" },
];

const STATS = [
  { k: "+45.000", v: "socios confían en nosotros" },
  { k: "100%", v: "digital, sin filas ni papeleo" },
  { k: "24/7", v: "acompañamiento con tu asesor" },
  { k: "CMF", v: "entidad fiscalizada en Chile" },
];

const BENEFITS = [
  {
    icon: Lightning,
    title: "Aprobación ágil",
    desc: "Evaluamos tu solicitud en tiempo récord y sin trámites interminables. Respuesta clara y rápida.",
  },
  {
    icon: Scales,
    title: "Condiciones transparentes",
    desc: "Tasas y cuotas claras desde el primer día. Sin letra chica ni costos ocultos en tu crédito.",
  },
  {
    icon: ChartLineUp,
    title: "Crece tu capital",
    desc: "Asesoría financiera enfocada en hacer rendir tu patrimonio con una estrategia a tu medida.",
  },
  {
    icon: Handshake,
    title: "Asesor dedicado",
    desc: "Una persona real te acompaña en cada paso, por chat dentro de la app web cuando lo necesites.",
  },
  {
    icon: Lock,
    title: "Seguridad primero",
    desc: "Tus datos y operaciones protegidos con los más altos estándares de seguridad y confidencialidad.",
  },
  {
    icon: Clock,
    title: "Disponible siempre",
    desc: "Gestiona tus créditos e inversiones desde cualquier lugar, a cualquier hora, en segundos.",
  },
];

const STEPS = [
  {
    icon: UserPlus,
    title: "Ingresa a tu cuenta",
    desc: "Accede con tu RUT y PIN en segundos. Todo 100% en línea y sin papeleo.",
  },
  {
    icon: FileText,
    title: "Simula o solicita",
    desc: "Pide tu crédito o explora opciones junto a tu asesor asignado.",
  },
  {
    icon: CheckCircle,
    title: "Recibe tu evaluación",
    desc: "Te entregamos una propuesta clara con tasas, cuotas y plazos a tu medida.",
  },
  {
    icon: Coins,
    title: "Haz crecer tu dinero",
    desc: "Recibe tu crédito o activa tu inversión y sigue todo desde tu panel digital.",
  },
];

const TESTIMONIALS = [
  {
    name: "Camila Fuentes",
    role: "Emprendedora · Valparaíso",
    initials: "CF",
    text: "Pedí un crédito para mi local y en el mismo día tenía respuesta. El asesor me explicó todo con cero letra chica. Excelente experiencia.",
  },
  {
    name: "Rodrigo Pérez",
    role: "Ingeniero · Santiago",
    initials: "RP",
    text: "Lo mejor es poder simular las cuotas yo mismo antes de decidir. Todo digital, sin perder tiempo en filas. 100% recomendado.",
  },
  {
    name: "Valentina Soto",
    role: "Profesora · Concepción",
    initials: "VS",
    text: "Tenía dudas por ser mi primer crédito, pero el acompañamiento por chat fue clave. Siempre hubo alguien para responderme.",
  },
];

const FAQ = [
  {
    q: "¿Qué necesito para solicitar un crédito?",
    a: "Solo tu RUT y tu PIN de acceso. El proceso es 100% digital: ingresas a tu cuenta, simulas el monto y las cuotas que necesitas, y tu asesor evalúa la solicitud. Sin papeleo ni filas.",
  },
  {
    q: "¿Cuánto demora la aprobación?",
    a: "Buscamos entregarte una respuesta el mismo día. Una vez que envías tu solicitud, tu asesor la revisa y te contacta con una propuesta clara de tasas, cuotas y plazos.",
  },
  {
    q: "¿Las tasas y cuotas son claras?",
    a: "Totalmente. Antes de aceptar verás el detalle completo: cuota mensual, total a pagar e intereses. Sin costos ocultos ni letra chica. Puedes simularlo tú mismo cuando quieras.",
  },
  {
    q: "¿EFE - CAPITAL es una entidad regulada?",
    a: "Sí. Operamos como entidad fiscalizada por la Comisión para el Mercado Financiero (CMF) de Chile, cumpliendo los estándares de seguridad y transparencia del sistema financiero.",
  },
  {
    q: "¿Puedo hablar con una persona real?",
    a: "Siempre. Cada cliente cuenta con un asesor dedicado disponible por chat dentro de la app, correo o WhatsApp para acompañarte en cada paso.",
  },
];

function Logo({ className = "", light = false }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <img
        src="/logo-efe.png"
        alt="EFE - CAPITAL"
        className="h-10 w-10 rounded-lg object-contain"
      />
      <div className="leading-none">
        <div
          className="text-[1.15rem] font-extrabold tracking-tight"
          style={{ color: light ? "#FFFFFF" : BRAND.deep, fontFamily: "Outfit, sans-serif" }}
        >
          EFE
        </div>
        <div
          className="mt-0.5 text-[0.58rem] font-semibold tracking-[0.3em]"
          style={{ color: light ? "rgba(255,255,255,0.7)" : BRAND.bright, fontFamily: "Outfit, sans-serif" }}
        >
          CAPITAL
        </div>
      </div>
    </div>
  );
}

/* ====== Simulador de crédito interactivo ====== */
const MONTHLY_RATE = 2.4; // tasa mensual referencial

function SimuladorSection() {
  const [monto, setMonto] = useState(3000000);
  const [cuotas, setCuotas] = useState(24);

  const res = useMemo(() => simulate(monto, MONTHLY_RATE, cuotas), [monto, cuotas]);

  return (
    <section id="simulador" className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <span
            className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold"
            style={{ background: BRAND.soft, color: BRAND.deep }}
          >
            Simulador
          </span>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: BRAND.deep }}>
            Calcula tu crédito en segundos
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Mueve los controles para ver tu cuota mensual al instante. Transparente, sin compromiso y sin letra chica.
          </p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-5">
          {/* Controles */}
          <div className="rounded-3xl border border-slate-100 bg-[#F8FBF9] p-7 shadow-sm lg:col-span-3 md:p-9">
            <div className="flex items-center gap-3">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-2xl"
                style={{ background: BRAND.soft }}
              >
                <Calculator weight="fill" className="h-5 w-5" style={{ color: BRAND.deep }} />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-500">Personaliza tu crédito</div>
                <div className="text-xs text-slate-400">Tasa referencial {MONTHLY_RATE}% mensual</div>
              </div>
            </div>

            {/* Monto */}
            <div className="mt-8">
              <div className="flex items-end justify-between">
                <label className="text-sm font-semibold text-slate-600">Monto del crédito</label>
                <span className="text-2xl font-extrabold" style={{ color: BRAND.deep }}>
                  {formatCLP(monto)}
                </span>
              </div>
              <input
                type="range"
                min={500000}
                max={20000000}
                step={100000}
                value={monto}
                onChange={(e) => setMonto(Number(e.target.value))}
                className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200"
                style={{ accentColor: BRAND.bright }}
              />
              <div className="mt-2 flex justify-between text-xs text-slate-400">
                <span>$ 500.000</span>
                <span>$ 20.000.000</span>
              </div>
            </div>

            {/* Cuotas */}
            <div className="mt-8">
              <div className="flex items-end justify-between">
                <label className="text-sm font-semibold text-slate-600">Número de cuotas</label>
                <span className="text-2xl font-extrabold" style={{ color: BRAND.deep }}>
                  {cuotas} <span className="text-base font-semibold text-slate-400">meses</span>
                </span>
              </div>
              <input
                type="range"
                min={6}
                max={48}
                step={1}
                value={cuotas}
                onChange={(e) => setCuotas(Number(e.target.value))}
                className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200"
                style={{ accentColor: BRAND.bright }}
              />
              <div className="mt-2 flex justify-between text-xs text-slate-400">
                <span>6 meses</span>
                <span>48 meses</span>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {[1000000, 3000000, 5000000, 10000000].map((m) => (
                <button
                  key={m}
                  onClick={() => setMonto(m)}
                  className="rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors"
                  style={
                    monto === m
                      ? { background: BRAND.deep, color: "#fff", borderColor: BRAND.deep }
                      : { borderColor: "#D7E3DB", color: BRAND.deep }
                  }
                >
                  {formatThousands(m)}
                </button>
              ))}
            </div>
          </div>

          {/* Resultado */}
          <div
            className="relative overflow-hidden rounded-3xl p-7 text-white shadow-xl lg:col-span-2 md:p-9"
            style={{ background: `linear-gradient(150deg, ${BRAND.deep} 0%, ${BRAND.deep2} 100%)` }}
          >
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full blur-3xl opacity-30"
              style={{ background: BRAND.bright }}
            />
            <div className="relative">
              <div className="text-sm font-semibold text-emerald-50/80">Tu cuota mensual estimada</div>
              <div className="mt-2 text-4xl font-extrabold tracking-tight md:text-5xl">
                {formatCLP(res.monthly)}
              </div>
              <div className="mt-1 text-sm text-emerald-50/70">en {cuotas} cuotas iguales</div>

              <div className="mt-8 space-y-3 border-t border-white/15 pt-6 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-emerald-50/80">Monto solicitado</span>
                  <span className="font-semibold">{formatCLP(res.principal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-emerald-50/80">Interés total</span>
                  <span className="font-semibold">{formatCLP(res.interest)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-emerald-50/80">Total a pagar</span>
                  <span className="font-semibold">{formatCLP(res.total)}</span>
                </div>
              </div>

              <Link
                to="/login"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-bold transition-transform hover:-translate-y-0.5"
                style={{ color: BRAND.deep }}
              >
                Solicitar este crédito
                <ArrowRight weight="bold" className="h-5 w-5" />
              </Link>
              <p className="mt-3 text-center text-[0.7rem] leading-relaxed text-emerald-50/60">
                Simulación referencial. El valor final depende de la evaluación de tu asesor.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ====== FAQ acordeón ====== */
function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="preguntas" className="py-20 md:py-28">
      <div className="mx-auto max-w-3xl px-5">
        <div className="text-center">
          <span
            className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold"
            style={{ background: BRAND.soft, color: BRAND.deep }}
          >
            Preguntas frecuentes
          </span>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: BRAND.deep }}>
            Resolvemos tus dudas
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Todo lo que necesitas saber antes de dar el siguiente paso con EFE - CAPITAL.
          </p>
        </div>

        <div className="mt-12 space-y-3">
          {FAQ.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                className="overflow-hidden rounded-2xl border bg-white transition-colors"
                style={{ borderColor: isOpen ? BRAND.leaf : "#E6ECE8" }}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="text-base font-bold" style={{ color: BRAND.ink }}>
                    {f.q}
                  </span>
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    style={{ background: isOpen ? BRAND.deep : BRAND.soft, color: isOpen ? "#fff" : BRAND.deep }}
                  >
                    {isOpen ? <Minus weight="bold" className="h-4 w-4" /> : <Plus weight="bold" className="h-4 w-4" />}
                  </span>
                </button>
                <div
                  className="grid transition-all duration-300 ease-in-out"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 leading-relaxed text-slate-600">{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function LandingActiva() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className="min-h-screen overflow-x-hidden antialiased"
      style={{ background: BRAND.bg, color: BRAND.ink, fontFamily: "Outfit, sans-serif" }}
    >
      {/* ================= NAVBAR ================= */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5">
          <Logo />

          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-sm font-medium text-slate-600 transition-colors hover:text-[var(--deep)]"
                style={{ "--deep": BRAND.deep }}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <Link
              to="/login"
              className="group inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
              style={{ background: BRAND.deep, boxShadow: "0 10px 25px -8px rgba(11,122,62,0.5)" }}
            >
              Ingresar
              <ArrowRight weight="bold" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            className="rounded-lg p-2 text-slate-700 lg:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menú"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <List className="h-6 w-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-100 bg-white px-5 py-4 lg:hidden">
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {n.label}
                </a>
              ))}
              <Link
                to="/login"
                className="mt-2 flex items-center justify-center gap-2 rounded-full px-4 py-3 text-center text-sm font-semibold text-white"
                style={{ background: BRAND.deep }}
              >
                <SignIn weight="bold" className="h-4 w-4" />
                Ingresar a mi cuenta
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
        <div
          className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full blur-3xl opacity-25"
          style={{ background: BRAND.bright }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 left-[-10%] h-[420px] w-[420px] rounded-full blur-3xl opacity-20"
          style={{ background: BRAND.deep }}
        />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2">
          <div>
            <span
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold"
              style={{ background: BRAND.soft, color: BRAND.deep }}
            >
              <ShieldCheck weight="fill" className="h-4 w-4" />
              Abre tu cuenta gratis · 100% en línea
            </span>

            <h1
              className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight md:text-6xl"
              style={{ color: BRAND.deep }}
            >
              Abre tu cuenta digital y{" "}
              <span className="relative whitespace-nowrap">
                <span style={{ color: BRAND.bright }}>deposita en segundos</span>
              </span>
              .
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              Crea tu cuenta <strong style={{ color: BRAND.deep }}>EFE - CAPITAL</strong> en menos de 2 minutos, haz tu
              primer depósito al instante y accede a créditos a tu medida. Sin filas, sin papeleo y con un asesor
              dedicado a tu lado.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/register"
                className="group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold text-white transition-transform hover:-translate-y-0.5"
                style={{ background: BRAND.deep, boxShadow: "0 18px 35px -12px rgba(11,122,62,0.55)" }}
              >
                Abrir mi cuenta gratis
                <ArrowRight weight="bold" className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-7 py-3.5 text-base font-semibold transition-colors hover:bg-white"
                style={{ borderColor: BRAND.deep, color: BRAND.deep }}
              >
                <SignIn weight="bold" className="h-5 w-5" />
                Ya tengo cuenta
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle weight="fill" className="h-4 w-4" style={{ color: BRAND.bright }} /> Cuenta 100% gratis
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle weight="fill" className="h-4 w-4" style={{ color: BRAND.bright }} /> Primer depósito en segundos
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle weight="fill" className="h-4 w-4" style={{ color: BRAND.bright }} /> Sin papeleo ni filas
              </span>
            </div>
          </div>

          {/* imagen hero */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl" style={{ boxShadow: "0 40px 80px -30px rgba(8,62,34,0.45)" }}>
              <img
                src="https://images.unsplash.com/photo-1781246435700-afec19012b45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBzdWNjZXNzfGVufDB8fHxncmVlbnwxNzkyNDI5OTc3fDA&ixlib=rb-4.1.0&q=85"
                alt="Crecimiento financiero EFE Capital"
                className="h-[420px] w-full object-cover md:h-[500px]"
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(160deg, rgba(8,62,34,0) 40%, rgba(8,62,34,0.4) 100%)" }}
              />
            </div>

            {/* tarjeta flotante */}
            <div className="absolute -bottom-6 -left-4 w-56 rounded-2xl bg-white p-4 shadow-xl md:-left-8">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: BRAND.soft }}>
                  <HandCoins weight="fill" className="h-5 w-5" style={{ color: BRAND.deep }} />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Crédito aprobado</div>
                  <div className="text-lg font-extrabold" style={{ color: BRAND.deep }}>
                    $ 5.000.000
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -right-3 top-8 rounded-2xl bg-white px-4 py-3 shadow-xl md:-right-6">
              <div className="flex items-center gap-2">
                <ChartLineUp weight="fill" className="h-5 w-5" style={{ color: BRAND.bright }} />
                <span className="text-sm font-bold" style={{ color: BRAND.deep }}>
                  Cuota desde $ 89.900
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="relative mx-auto mt-20 max-w-6xl px-5">
          <div className="grid grid-cols-2 gap-4 rounded-3xl bg-white p-6 shadow-lg md:grid-cols-4 md:p-8">
            {STATS.map((s) => (
              <div key={s.v} className="text-center">
                <div className="text-2xl font-extrabold md:text-3xl" style={{ color: BRAND.deep }}>
                  {s.k}
                </div>
                <div className="mt-1 text-xs text-slate-500 md:text-sm">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= BENEFICIOS ================= */}
      <section id="beneficios" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <span
              className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold"
              style={{ background: BRAND.soft, color: BRAND.deep }}
            >
              Beneficios
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: BRAND.deep }}>
              Por qué elegir EFE - CAPITAL
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Diseñamos cada producto pensando en tu tranquilidad: rapidez, claridad y cercanía en un solo lugar.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                className="group rounded-2xl border border-slate-100 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div
                  className="flex items-center justify-center rounded-2xl transition-colors"
                  style={{ background: BRAND.soft, width: "3.25rem", height: "3.25rem" }}
                >
                  <b.icon weight="fill" className="h-6 w-6" style={{ color: BRAND.deep }} />
                </div>
                <h3 className="mt-5 text-xl font-bold" style={{ color: BRAND.deep }}>
                  {b.title}
                </h3>
                <p className="mt-2 leading-relaxed text-slate-600">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* banda imagen + texto */}
          <div
            className="mt-16 grid items-center gap-8 overflow-hidden rounded-3xl p-2 md:grid-cols-2"
            style={{ background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.deep2} 100%)` }}
          >
            <div className="p-8 md:p-12">
              <h3 className="text-2xl font-extrabold text-white md:text-3xl">
                Tu patrimonio, trabajando para ti.
              </h3>
              <p className="mt-4 text-emerald-50/90">
                Nuestra asesoría financiera busca que tu capital crezca de forma sostenida, con estrategias claras y
                acompañamiento constante. Tú decides, nosotros te guiamos.
              </p>
              <Link
                to="/login"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                style={{ color: BRAND.deep }}
              >
                Ingresar ahora
                <ArrowRight weight="bold" className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative h-64 md:h-80">
              <img
                src="https://images.unsplash.com/photo-1758519289013-9442fb2aeac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBhZHZpc29yfGVufDB8fHxncmVlbnwxNzkyNDI5OTc2fDA&ixlib=rb-4.1.0&q=85"
                alt="Asesoría financiera EFE Capital"
                className="h-full w-full rounded-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= CÓMO FUNCIONA ================= */}
      <section id="como-funciona" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <span
              className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold"
              style={{ background: BRAND.soft, color: BRAND.deep }}
            >
              Cómo funciona
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: BRAND.deep }}>
              Tu dinero en marcha en 4 pasos
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Un proceso simple y 100% digital, desde que ingresas hasta que tu capital empieza a crecer.
            </p>
          </div>

          <div className="relative mt-16 grid gap-8 md:grid-cols-4">
            <div
              className="absolute left-0 right-0 top-7 hidden h-0.5 md:block"
              style={{ background: `linear-gradient(90deg, ${BRAND.soft}, ${BRAND.bright}, ${BRAND.soft})` }}
            />
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative text-center">
                <div
                  className="relative z-10 mx-auto flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg"
                  style={{ background: BRAND.deep }}
                >
                  <s.icon weight="fill" className="h-7 w-7" />
                  <span
                    className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white ring-2 ring-white"
                    style={{ background: BRAND.bright }}
                  >
                    {i + 1}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-bold" style={{ color: BRAND.deep }}>
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/login"
              className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform hover:-translate-y-0.5"
              style={{ background: BRAND.deep, boxShadow: "0 18px 35px -12px rgba(11,122,62,0.55)" }}
            >
              Ingresar a mi cuenta
              <ArrowRight weight="bold" className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ================= SIMULADOR ================= */}
      <SimuladorSection />

      {/* ================= TESTIMONIOS ================= */}
      <section id="testimonios" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <span
              className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold"
              style={{ background: BRAND.soft, color: BRAND.deep }}
            >
              Testimonios
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: BRAND.deep }}>
              Historias reales de nuestros socios
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Miles de personas ya confían en EFE - CAPITAL para dar el siguiente paso financiero.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                className="relative rounded-2xl border border-slate-100 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <Quotes weight="fill" className="h-8 w-8 opacity-20" style={{ color: BRAND.bright }} />
                <p className="mt-3 leading-relaxed text-slate-700">{t.text}</p>
                <div className="mt-5 flex items-center gap-1" style={{ color: "#F5B301" }}>
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} weight="fill" className="h-4 w-4" />
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-5">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white"
                    style={{ background: BRAND.deep }}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <div className="font-bold" style={{ color: BRAND.ink }}>
                      {t.name}
                    </div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PREGUNTAS (FAQ) ================= */}
      <FaqSection />

      {/* ================= CTA + CONTACTO ================= */}
      <section id="contacto" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div
            className="relative overflow-hidden rounded-[2.5rem] px-6 py-14 md:px-16 md:py-20"
            style={{ background: `linear-gradient(140deg, ${BRAND.deep} 0%, ${BRAND.deep2} 100%)` }}
          >
            <div
              className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full blur-3xl opacity-25"
              style={{ background: BRAND.bright }}
            />
            <div className="relative grid gap-10 lg:grid-cols-2">
              <div>
                <h2 className="text-3xl font-extrabold leading-tight text-white md:text-4xl">
                  Tu cuenta digital, lista en 2 minutos.
                </h2>
                <p className="mt-4 max-w-md text-emerald-50/90">
                  Ábrela gratis, haz tu primer depósito en segundos y empieza a mover tu dinero hoy mismo. Todo 100% en
                  línea, simple y seguro, sin trámites presenciales.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold transition-transform hover:-translate-y-0.5"
                    style={{ color: BRAND.deep }}
                  >
                    Abrir mi cuenta gratis
                    <ArrowRight weight="bold" className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/40 px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
                  >
                    <SignIn weight="bold" className="h-5 w-5" />
                    Ya tengo cuenta
                  </Link>
                </div>
              </div>

              <div className="grid gap-4 self-center">
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-4 rounded-2xl bg-white/10 p-4 backdrop-blur transition-colors hover:bg-white/15"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                    <EnvelopeSimple weight="fill" className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-50/70">Correo</div>
                    <div className="font-semibold text-white">{EMAIL}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-2xl bg-white/10 p-4 backdrop-blur">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                    <Headset weight="fill" className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-50/70">Atención</div>
                    <div className="font-semibold text-white">Lun a Vie · 9:00 a 19:00 hrs</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-2xl bg-white/10 p-4 backdrop-blur">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                    <Globe weight="fill" className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-50/70">Todo online</div>
                    <div className="font-semibold text-white">Opera 100% desde la app web</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <Logo />
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
                Créditos ágiles y asesoría financiera inteligente. Capital que crece contigo.
              </p>
            </div>

            <div className="text-sm text-slate-600">
              <h4 className="font-bold" style={{ color: BRAND.deep }}>
                Contacto
              </h4>
              <ul className="mt-4 space-y-2.5">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" /> Teléfono {CMF.telefono}
                </li>
                <li className="flex items-center gap-2">
                  <Printer className="h-4 w-4 text-slate-400" /> Fax {CMF.fax}
                </li>
                <li className="flex items-center gap-2">
                  <EnvelopeSimple className="h-4 w-4 text-slate-400" /> {EMAIL}
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  {CMF.domicilio}
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-slate-400" /> Operación 100% en línea
                </li>
              </ul>
            </div>

            <div className="text-sm text-slate-600 md:col-span-2">
              <h4 className="font-bold" style={{ color: BRAND.deep }}>
                Información oficial EFE
              </h4>
              <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-slate-400">Razón social</dt>
                  <dd className="font-medium">{CMF.razon}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">RUT</dt>
                  <dd className="font-medium">{CMF.rut}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">N° de Inscripción</dt>
                  <dd className="font-medium">{CMF.inscripcion}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Fecha de Inscripción</dt>
                  <dd className="font-medium">{CMF.fecha}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Tipo de entidad</dt>
                  <dd className="font-medium">{CMF.tipo}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Vigencia</dt>
                  <dd className="inline-flex items-center gap-1 font-medium" style={{ color: BRAND.deep }}>
                    <SealCheck weight="fill" className="h-4 w-4" />
                    {CMF.vigencia}
                  </dd>
                </div>
              </dl>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <a
                  href={CMF_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors hover:bg-[#F0F7F2]"
                  style={{ borderColor: BRAND.deep, color: BRAND.deep }}
                >
                  <ShieldCheck weight="fill" className="h-4 w-4" />
                  Ver registro en la CMF
                  <ArrowUpRight weight="bold" className="h-3.5 w-3.5" />
                </a>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold text-white"
                  style={{ background: BRAND.deep }}
                >
                  <SignIn weight="bold" className="h-4 w-4" />
                  Ingresar
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} EFE - CAPITAL · EFE ARICA - LA PAZ S.A. — RUT 96.769.070-8. Todos los derechos
            reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
